import {
  FontWeight,
  IconRecurring,
  Select,
  type SelectOption,
} from '@leland-dev/leland-ui-library';
import { type FC, useCallback, useEffect, useMemo } from 'react';

import { RecurrenceType } from '../../../../__generated-gql-types__/globalTypes';
import { formatOrdinal, getDateInfo } from '../../../../utils/session';

import { type RecurringSessionSelectRecurringCadenceFragment } from './__generated-gql-types__/RecurringSessionSelect.generated';

interface RecurringSessionSelectProps {
  value: Possible<RecurringSessionSelectRecurringCadenceFragment>;
  onChange: (
    value: Possible<RecurringSessionSelectRecurringCadenceFragment>,
  ) => void;
  startAt: Date;
  durationMinutes: number;
  disabled?: boolean;
}

type RecurringSessionSelectOption =
  | RecurrenceType
  | 'none'
  | 'bi-weekly'
  | 'last-week';

export const RecurringSessionSelect: FC<RecurringSessionSelectProps> = ({
  value,
  onChange,
  startAt,
  durationMinutes,
  disabled,
}) => {
  const dateInfo = getDateInfo(startAt, durationMinutes);

  useEffect(() => {
    if (disabled) {
      onChange(null);
    }
  }, [disabled, onChange]);

  const onSelectChange = useCallback(
    (value: RecurringSessionSelectOption) => {
      if (value === 'none') {
        return onChange(null);
      }

      if (value === 'bi-weekly') {
        return onChange({
          type: RecurrenceType.WEEKLY,
          startHour: dateInfo.startHour,
          startMinute: dateInfo.startMinute,
          durationMinutes: dateInfo.durationMinutes,
          dayOfWeek: dateInfo.dayOfWeek,
          isLastWeekOfMonth: false,
          interval: 2,
        });
      }

      if (value === 'last-week') {
        return onChange({
          type: RecurrenceType.MONTHLY,
          startHour: dateInfo.startHour,
          startMinute: dateInfo.startMinute,
          durationMinutes: dateInfo.durationMinutes,
          dayOfWeek: dateInfo.dayOfWeek,
          weekOfMonth: dateInfo.weekOfMonth,
          isLastWeekOfMonth: true,
          interval: 1,
        });
      }

      onChange({
        type: value,
        startHour: dateInfo.startHour,
        startMinute: dateInfo.startMinute,
        durationMinutes: dateInfo.durationMinutes,
        dayOfWeek: dateInfo.dayOfWeek,
        isLastWeekOfMonth: false,
        weekOfMonth:
          value === RecurrenceType.MONTHLY ? dateInfo.weekOfMonth : null,
        interval: 1,
      });
    },
    [onChange, dateInfo],
  );

  const options: Array<SelectOption<RecurringSessionSelectOption>> = [
    {
      label: 'Does not repeat',
      value: 'none',
    },
    {
      label: 'Every day',
      value: RecurrenceType.DAILY,
    },
    {
      label: `Every week on ${dateInfo.weekdayName}`,
      value: RecurrenceType.WEEKLY,
    },
    {
      label: `Every 2 weeks on ${dateInfo.weekdayName}`,
      value: 'bi-weekly',
    },
    ...(dateInfo.weekOfMonth !== 5
      ? [
          {
            label: `Every month on the ${formatOrdinal(dateInfo.weekOfMonth)} ${dateInfo.weekdayName}`,
            value: RecurrenceType.MONTHLY,
          },
        ]
      : []),
    ...([4, 5].includes(dateInfo.weekOfMonth)
      ? ([
          {
            label: `Every month on the last ${dateInfo.weekdayName}`,
            value: 'last-week',
          },
        ] as const)
      : []),
  ];

  const selectedOption: RecurringSessionSelectOption = useMemo(() => {
    if (!value) {
      return 'none';
    }
    if (value.type === RecurrenceType.WEEKLY && value.interval === 2) {
      return 'bi-weekly';
    }
    if (value.type === RecurrenceType.MONTHLY && value.isLastWeekOfMonth) {
      return 'last-week';
    }
    return value.type;
  }, [value]);

  return (
    <div className="space-y-1">
      <Select
        label=""
        options={options}
        value={selectedOption}
        onValueChange={onSelectChange}
        fontWeight={FontWeight.MEDIUM}
        disabled={disabled}
        ClosedLeftIcon={IconRecurring}
      />
      {selectedOption !== 'none' ? (
        <p className="text-leland-gray-light">
          Future sessions will be scheduled using your available time balance.
          If your balance is insufficient, you&apos;ll be asked to pay for that
          session.
        </p>
      ) : null}
    </div>
  );
};
